import { useState, useRef, useEffect } from 'react';
import { cn } from '@/shared/lib/cn';

interface Option<T extends string> {
  value: T;
  label: string;
}

interface GlassSelectProps<T extends string> {
  value: T;
  options: Option<T>[];
  onChange: (v: T) => void;
  className?: string;
}

export function GlassSelect<T extends string>({ value, options, onChange, className }: GlassSelectProps<T>) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const current = options.find((o) => o.value === value);

  useEffect(() => {
    if (!open) return;
    function onOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onOutside);
    return () => document.removeEventListener('mousedown', onOutside);
  }, [open]);

  return (
    <div ref={ref} className={cn('relative w-full', className)}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center justify-center gap-1 px-3 py-[13px] w-full text-[18px] font-semibold text-text-primary min-w-[56px] rounded-[14px] bg-white/55 border border-white/60 backdrop-blur-[10px] transition-all active:scale-[0.97]"
      >
        <span>{current?.label ?? value}</span>
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          className={cn('text-text-muted transition-transform', open && 'rotate-180')}
        >
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>

      {open && (
        <div className="absolute top-[calc(100%+4px)] left-0 right-0 z-20 rounded-[12px] glass-content shadow-[0_8px_24px_rgba(18,52,36,0.16)] overflow-hidden min-w-[72px]">
          {options.map((opt) => (
            <button
              key={opt.value}
              type="button"
              className={cn(
                'w-full text-center px-3 py-[10px] text-[16px] font-medium transition-colors',
                opt.value === value
                  ? 'text-brand-teal font-semibold bg-brand-green/10'
                  : 'text-text-primary hover:bg-white/40',
              )}
              onClick={() => { onChange(opt.value); setOpen(false); }}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
